import {
  Download,
  FileImage,
  FileSpreadsheet,
  FileText,
  Loader2,
  type LucideIcon,
} from 'lucide-react';
import React, { useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SimpleTooltip } from '@/components/ui/tooltip';

export type ChartDownloadFormat = 'csv' | 'xlsx' | 'pdf';

export interface ChartDownloadMenuProps {
  /** Receives the chosen format; implementation lives at the callsite (Phase 6). */
  onDownload: (format: ChartDownloadFormat) => void | Promise<void>;
  disabled?: boolean;
  formats?: ChartDownloadFormat[];
  ariaLabel?: string;
  tooltip?: string;
}

const DOWNLOAD_OPTIONS: Array<{
  format: ChartDownloadFormat;
  label: string;
  Icon: LucideIcon;
}> = [
  { format: 'csv', label: 'CSV', Icon: FileText },
  { format: 'xlsx', label: 'Excel', Icon: FileSpreadsheet },
  { format: 'pdf', label: 'PDF', Icon: FileImage },
];

export const ChartDownloadMenu: React.FC<ChartDownloadMenuProps> = ({
  onDownload,
  disabled,
  formats,
  ariaLabel = 'Download chart data',
  tooltip = 'Download',
}) => {
  const [open, setOpen] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState<ChartDownloadFormat | null>(null);
  const isDownloadingRef = useRef(false);
  const isDownloading = downloadingFormat != null;
  const visibleOptions = useMemo(
    () => DOWNLOAD_OPTIONS.filter(option => !formats || formats.includes(option.format)),
    [formats],
  );

  const handleDownload = (format: ChartDownloadFormat) => {
    if (disabled || isDownloadingRef.current) return;

    isDownloadingRef.current = true;
    setOpen(true);
    setDownloadingFormat(format);

    void (async () => {
      try {
        await onDownload(format);
      } finally {
        isDownloadingRef.current = false;
        setDownloadingFormat(null);
        setOpen(false);
      }
    })();
  };

  const menu = (
    <DropdownMenu
      open={open}
      onOpenChange={nextOpen => {
        if (!isDownloadingRef.current) setOpen(nextOpen);
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled || isDownloading}
          aria-label={ariaLabel}
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
        >
          {isDownloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {visibleOptions.map(({ format, label, Icon }) => (
          <DropdownMenuItem
            key={format}
            disabled={isDownloading}
            onSelect={event => {
              event.preventDefault();
              handleDownload(format);
            }}
            className="flex items-center gap-2"
          >
            {downloadingFormat === format ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Icon className="h-4 w-4" />
            )}
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (open || isDownloading) return menu;

  return (
    <SimpleTooltip content={tooltip} side="top">
      {menu}
    </SimpleTooltip>
  );
};
